import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import {
  RateReview,
  StarOutline,
  PeopleOutline,
  TodayOutlined,
} from "@mui/icons-material";
import {
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ExportButton from "../../components/ag grid/ExportButton";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ListPageLayout from "../../components/layout/ListPageLayout";
import AppraisalViewController from "../../redux/actions/AppraisalAction";
import AppraisalReviewFormModal from "./AddAppraisalReview";
import AppraisalReviewMobileView from "./AppraisalReviewMobileView";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import "../../styles/styles.css";
import "../../styles/layout.css";

const AppraisalReviewList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [showModal, setShowModal] = useState(false);
  const [editCycle, setEditCycle] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const { getAppraisalReview, deleteAppraisalCycle } = AppraisalViewController();
  const { appraisal_review } = useSelector((state) => state.apprisalcycle);

  useEffect(() => {
    dispatch(getAppraisalReview());
  }, [dispatch]);

  const handleDelete = useCallback(
    (id) => dispatch(deleteAppraisalCycle(id)),
    [dispatch]
  );

  const handleEdit = useCallback((cycle) => {
    setEditCycle(cycle);
    setShowModal(true);
  }, []);

  const handleToggleSelect = useCallback((row) => {
    setSelectedRows((prev) => {
      const exists = prev.some((item) => item.id === row.id);
      if (exists) {
        return prev.filter((item) => item.id !== row.id);
      }
      return [...prev, row];
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedRows([]);
    gridRef.current?.deselectAll?.();
  }, []);

  const handleMobileExport = useCallback(() => {
    if (selectedRows.length === 0) {
      alert("Please select rows to export.");
      return;
    }

    const exportColumns = [
      "employee_id",
      "employee_name",
      "appraisal_period",
      "rating",
      "review_date",
    ];

    const filteredRows = selectedRows.map((row) => {
      const filtered = {};
      exportColumns.forEach((key) => {
        filtered[key] = row[key];
      });
      return filtered;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "AppraisalReviews.xlsx");
  }, [selectedRows]);

  const openAddModal = useCallback(() => {
    setEditCycle(null);
    setShowModal(true);
  }, []);

  const finalReviews = useMemo(() => {
    const list = appraisal_review || [];
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter(
      (item) =>
        item.employee_name?.toLowerCase().includes(q) ||
        item.employee_id?.toLowerCase().includes(q) ||
        item.appraisal_period?.toLowerCase().includes(q)
    );
  }, [appraisal_review, searchTerm]);

  const stats = useMemo(() => {
    const list = appraisal_review || [];
    const ratings = list
      .map((r) => Number(r.rating))
      .filter((n) => !Number.isNaN(n));
    const avgRating =
      ratings.length > 0
        ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1)
        : "0";
    const today = new Date().toISOString().slice(0, 10);
    const todayCount = list.filter((r) => r.review_date === today).length;

    return {
      total: list.length,
      avgRating,
      uniqueEmployees: new Set(list.map((r) => r.employee_id)).size,
      todayCount,
    };
  }, [appraisal_review]);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "EMP Id",
        field: "employee_id",
        width: 100,
        minWidth: 90,
        maxWidth: 120,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee",
        field: "employee_name",
        flex: 1.5,
        minWidth: 160,
      },
      {
        headerName: "Appraisal Period",
        field: "appraisal_period",
        flex: 1.5,
        minWidth: 180,
      },
      {
        headerName: "Rating",
        field: "rating",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        suppressSizeToFit: true,
        cellRenderer: (params) => (
          <span
            style={{
              fontWeight: 700,
              color: "#1A2B4A",
            }}
          >
            {params.value ?? "-"}
          </span>
        ),
      },
      {
        headerName: "Review Date",
        field: "review_date",
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        suppressSizeToFit: true,
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(params.data)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                sx={{ color: "#d32f2f" }}
                onClick={() => handleDelete(params.data.id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleDelete, handleEdit]
  );

  return (
    <>
      {isMobile ? (
        <AppraisalReviewMobileView
          reviews={finalReviews}
          stats={stats}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={openAddModal}
          onExport={handleMobileExport}
          onClearSelection={handleClearSelection}
        />
      ) : (
      <ListPageLayout
        title="Appraisal Review"
        subtitle="Record and manage reviewer ratings for appraisal cycles"
        headerAction={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => {
              setEditCycle(null);
              setShowModal(true);
            }}
            sx={toolbarButtonSx}
          >
            Add Appraisal Review
          </Button>
        }
        toolbarRight={
          <>
            <ExportButton
              gridRef={gridRef}
              fileName="AppraisalReviews.xlsx"
              exportColumns={[
                "employee_id",
                "employee_name",
                "appraisal_period",
                "rating",
                "review_date",
              ]}
              disabled={selectedRows.length === 0}
              onSelectionCleared={() => setSelectedRows([])}
            />
            <TextField
              size="small"
              placeholder="Search reviews..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <PageSizeSelector
              pageSize={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            />
          </>
        }
        stats={
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <div className="stat-card stat-card-compact">
                <div className="avatar rounded-circle bg-primary">
                  <RateReview className="white-icon" />
                </div>
                <div className="stat-card-compact-body">
                  <h6>Total Reviews</h6>
                  <h3 className="value">{stats.total}</h3>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className="stat-card stat-card-compact">
                <div className="avatar rounded-circle bg-info">
                  <StarOutline className="white-icon" />
                </div>
                <div className="stat-card-compact-body">
                  <h6>Avg Rating</h6>
                  <h3 className="value">{stats.avgRating}</h3>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className="stat-card stat-card-compact">
                <div className="avatar rounded-circle bg-primary">
                  <PeopleOutline className="white-icon" />
                </div>
                <div className="stat-card-compact-body">
                  <h6>Employees Reviewed</h6>
                  <h3 className="value">{stats.uniqueEmployees}</h3>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className="stat-card stat-card-compact">
                <div className="avatar rounded-circle bg-danger">
                  <TodayOutlined className="white-icon" />
                </div>
                <div className="stat-card-compact-body">
                  <h6>Reviews Today</h6>
                  <h3 className="value">{stats.todayCount}</h3>
                </div>
              </div>
            </Grid>
          </Grid>
        }
      >
        <div className="list-page-grid">
          <ReusableGrid
            rowData={finalReviews}
            columnDefs={columnDefs}
            gridRef={gridRef}
            pagination
            pageSize={pageSize}
            onSelectionChanged={(rows) => setSelectedRows(rows)}
            emptyMessage="No appraisal reviews yet"
            emptyHint='Click "Add Appraisal Review" to submit the first review.'
          />
        </div>
      </ListPageLayout>
      )}

      {showModal && (
        <AppraisalReviewFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editCycle}
        />
      )}
    </>
  );
};

export default AppraisalReviewList;
